import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';
import Constants from '../../Constants';

/**
 * The `FirmTypeFilter` function is a React component that renders a filter for selecting fund types,
 * with options that can be selected or deselected by the user.
 * @returns The `FirmTypeFilter` component returns JSX elements that represent a filter for selecting
 * fund types. It includes a heading, a list item with a filter wrapper, and a multi-select entity
 * component. The filter options are rendered conditionally based on the `open` prop.
 */
function FirmTypeFilter({ FundsStageOptions, setFundsStageOptions, customPathname, label = "Firm Type", isPopupOpen, setIsPopupOpen }) {

    const { FundsStage, refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    /* The `useEffect` hook is used to perform side effects in a functional component. In this case,
    the `useEffect` hook is used to update the `FundsStageOptions` state based on the values in the
    `refineSearchFilters` and `FundsStage` contexts. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.fundStage?.map(value => value.id) ?? [];
        // VC funds are only the VC / VC (Seed) stages
        const stages = customPathname?.includes("vc/investors")
            ? FundsStage?.filter(stage => Constants.vcFundStageIds.includes(parseInt(stage.id)))
            : FundsStage
        setFundsStageOptions(stages?.map(industry => ({ ...industry, isChecked: industryIds.includes(parseInt(industry.id)) })))
    }, [FundsStage, customPathname, refineSearchFilters, setFundsStageOptions])

    /* The `handleFundTypeChange` function is a callback function that is triggered when the user
    selects or deselects a fund type option. It takes an event object `e` as a parameter, which
    represents the event that triggered the function (in this case, a checkbox change event). */
    const handleFundTypeChange = (e) => {
        const { name, checked, id, filteredIds } = e.target;
        let currentFundType = [];
    
        if (name === "allSelect") {
            currentFundType = FundsStageOptions.map((fundtype) =>
                ({ ...fundtype, isChecked: checked })
            );
        } else if (name === "filteredSelect" && filteredIds) {
            currentFundType = FundsStageOptions.map((fundtype) => {
                if (filteredIds.includes(fundtype.id)) {
                    return { ...fundtype, isChecked: checked };
                }
                return fundtype;
            });
        } else {
            currentFundType = FundsStageOptions.map((fundtype) =>
                fundtype.id === parseInt(id) ? { ...fundtype, isChecked: checked } : fundtype
            );
        }
    
        setFundsStageOptions(currentFundType);
    };

    const selectedLength = FundsStageOptions?.filter(option => option.isChecked)?.length ?? 0;

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {FundsStage.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-2">
                        <MultiSelectEntity
                            handleEntityChange={handleFundTypeChange}
                            primaryEntity={FundsStageOptions}
                        />
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default FirmTypeFilter;